<template>
	<div class="space-y-3">
		<!-- Label -->
		<label v-if="label" :for="id" class="block mb-2 text-sm font-semibold text-gray-900">
			{{ label }}
			<span v-if="required" class="text-red-500">*</span>
		</label>

		<!-- Options Grid/Stack -->
		<div
			:class="[
				'grid gap-3',
				layout === 'horizontal' ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1',
			]"
			role="radiogroup"
			:aria-labelledby="id"
		>
			<button
				v-for="option in options"
				:key="option.value"
				type="button"
				@click="selectOption(option.value)"
				:class="[
					'relative flex items-center justify-center px-4 py-4 text-left transition-all duration-200 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500',
					modelValue === option.value
						? 'border-blue-500 bg-blue-50 text-blue-900'
						: 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50',
					disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
				]"
				:disabled="disabled"
				:aria-checked="modelValue === option.value"
				role="radio"
			>
				<!-- Icon (optional) -->
				<span
					v-if="option.icon"
					class="flex items-center justify-center mr-3 text-2xl"
					aria-hidden="true"
				>
					{{ option.icon }}
				</span>

				<!-- Content -->
				<div class="flex-1 min-w-0">
					<div class="font-semibold">{{ option.label }}</div>
					<div v-if="option.description" class="mt-1 text-xs text-gray-600">
						{{ option.description }}
					</div>
				</div>

				<!-- Selected indicator -->
				<svg
					v-if="modelValue === option.value"
					class="flex-shrink-0 w-5 h-5 ml-3 text-blue-600"
					fill="currentColor"
					viewBox="0 0 20 20"
					aria-hidden="true"
				>
					<path
						fill-rule="evenodd"
						d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
						clip-rule="evenodd"
					/>
				</svg>
			</button>
		</div>

		<!-- Error message -->
		<p v-if="error" class="flex items-center mt-2 text-sm text-red-600">
			<svg
				class="flex-shrink-0 w-4 h-4 mr-1"
				fill="currentColor"
				viewBox="0 0 20 20"
				aria-hidden="true"
			>
				<path
					fill-rule="evenodd"
					d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
					clip-rule="evenodd"
				/>
			</svg>
			{{ error }}
		</p>

		<!-- Helper text -->
		<p v-if="helperText && !error" class="mt-2 text-sm text-gray-600">
			{{ helperText }}
		</p>
	</div>
</template>

<script setup lang="ts">
interface Option {
	value: string;
	label: string;
	description?: string;
	icon?: string; // Emoji or icon
}

interface Props {
	id?: string;
	label?: string;
	modelValue: string;
	options: Option[];
	layout?: 'horizontal' | 'vertical';
	required?: boolean;
	disabled?: boolean;
	error?: string;
	helperText?: string;
}

const props = withDefaults(defineProps<Props>(), {
	id: 'radio-group',
	layout: 'horizontal',
	required: false,
	disabled: false,
});

const emit = defineEmits<{
	'update:modelValue': [value: string];
	change: [value: string];
}>();

function selectOption(value: string) {
	if (props.disabled) return;
	emit('update:modelValue', value);
	emit('change', value);
}
</script>
